import CardState, { type CardStateValue } from '@/components/ui/CardState'
import Change from '@/components/ui/Change'
import Money from '@/components/ui/Money'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import SymbolLink from '@/components/ui/SymbolLink'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { usePopularStocks } from '@/features/watchlist/api/queries'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import type { WatchRow } from '@/schemas/watchlist'

/**
 * The MAXIMUM, not the count. Five is the live design's own (4236:69879) and
 * every row the movers feed carries (`watchlistService.ts`), so with room for
 * the whole rail this card still slices nothing off; the rail's fit draws
 * fewer, down to one, on a window too short for it.
 */
const RAIL_ROWS = 5

/** One row's box at `py-2` (16) around a 16px line, 32px — the row below. No logo slot:
 *  the live design draws a bare symbol (4236:69879). */
function StockRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <Skeleton className="h-4 w-16 rounded-xs" />
      <div className="flex shrink-0 items-center gap-2">
        <Skeleton className="h-4 w-14 rounded-xs" />
        <Skeleton className="h-4 w-12 rounded-xs" />
      </div>
    </div>
  )
}

/** The skeleton's rows, one per row the card can draw, trimmed by the same fit. */
const SKELETON_ROWS = Array.from({ length: RAIL_ROWS }, (_, index) => (
  <StockRowSkeleton key={index} />
))

/**
 * One Popular-stocks row: symbol, last price and change, one line, no logo.
 * Exported so a family rail (Stock, Portfolio) can reuse it rather than
 * re-deriving the same three-column line.
 *
 * The revamp mock's row (Home mock parity, 2026-10-28): 32px tall at 13/16
 * (`text-rail-row`), no hairlines between rows, the symbol 600 in the
 * foreground, the price in the secondary grey and the change in its own
 * fixed, right-aligned column (`w-16`) so the figures line up down the list.
 */
export function PopularStockRowItem({ row }: { row: WatchRow }) {
  return (
    // The symbol link stretches over the whole row (`after:inset-0`) so the row is one target
    // and its accessible name stays the bare symbol. The row takes the rail's hover wash
    // (`rail-row-wash`) and the price goes from secondary to foreground; the symbol underlines,
    // as the mock's does, because the stretched link IS the row's hover target.
    <div className="relative flex items-center justify-between gap-2 py-2 text-rail-row text-muted-foreground rail-row-wash hover:text-foreground focus-within:text-foreground">
      <SymbolLink
        symbol={row.symbol}
        className="truncate font-semibold text-foreground after:absolute after:inset-0 after:content-['']"
      />
      <span className="flex shrink-0 items-center gap-2">
        {/* No "Rs": the column is all one currency and the row has no room
            for five redundant prefixes. */}
        <Money value={row.ltp} />
        <Change value={row.percentChange} percent className="w-16 justify-end" />
      </span>
    </div>
  )
}

/**
 * The rail's popular-stocks block: up to five names — `RAIL_ROWS`, or as many
 * of them as the rail's fit leaves room for (`RailRows`), and none at all once
 * the fit drops the block on a very short window — each with its last price and
 * its move, one line, no logo. Flat, hairline-separated, a `SectionHead` +
 * `SeeAllLink` in place of the filled `SectionCard` this replaced, and no
 * closing footnote — the live design ends the list at its last row
 * (`4236:69879`, the 2026-09-28 design refresh).
 *
 * ── What "popular" is, and what it is not ─────────────────────────────────
 *
 * `usePopularStocks()` is reused verbatim (`features/watchlist/api/queries.ts:22`).
 * Despite living in the watchlist namespace it is not the member's list: the
 * service asks `marketEndpoints.movers({ type:'turnover', limit:5 })`, the
 * market-wide turnover leaderboard, from a `V5PublicAPIView` that takes no
 * user. So the rail can show it on every screen, signed in or not, and it
 * shares one cache entry with Home's own empty-watchlist card.
 *
 * ── No subtitle, on the card or on a row ──────────────────────────────────
 *
 * The design draws a bare header (no caption under "Popular stocks") and a
 * bare row (symbol and figure only, no company name) — V2 draws a distinct
 * editorial line under each symbol in some renders ("Book close on 7
 * Bhadra"), but `MoverRowSerializer` has no caption-like field and no
 * per-symbol editorial feed exists to supply one. Inventing captions — or
 * worse, deriving one from `change_percent` and getting it wrong on a
 * circuit-hit scrip — is the failure mode a bare row avoids. Backend ask
 * reported, not worked around.
 */
export default function PopularStocksCard() {
  const block = useRailBlock('stocks')
  const popular = usePopularStocks()
  const { data, isPending, isError } = popular
  const manual = useManualRefresh([popular])

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data.rows.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Popular stocks" {...block}>
      <SectionHead
        size="sm"
        title="Popular stocks"
        action={
          <SeeAllLink to="/market/movers" size="rail" aria-label="All stocks, by movers">
            All stocks
          </SeeAllLink>
        }
      />
      {/* Rows and skeleton rows both draw through `RailRows`, so the fit
          trims a loading list exactly as it will the loaded one. */}
      <div className="mt-1">
        <CardState
          state={state}
          skeleton={<RailRows block="stocks" rows={SKELETON_ROWS} />}
          onRetry={manual.refresh}
          failedTitle="Couldn't load the list"
          failedDescription="The movers feed is not answering. Try again."
          emptyTitle="No names traded yet"
          emptyDescription="The turnover board fills once the session opens."
        >
          {data === undefined ? null : (
            <RailRows
              block="stocks"
              rows={data.rows.slice(0, RAIL_ROWS).map((row) => (
                <PopularStockRowItem key={row.symbol} row={row} />
              ))}
            />
          )}
        </CardState>
      </div>
    </section>
  )
}
